import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Form, Button, Label, Input, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import moment from "moment";

/**
 * Factura un pedido, total o parcial, a nivel talla y color.
 *
 * La casa facturaba desde el surtido, asi que no se podia facturar antes de
 * entregar. Aqui la factura cuelga del pedido: se puede facturar antes o
 * despues de surtir, y facturar NO mueve inventario (el stock baja al surtir).
 */
function FacturasPedidoCreate() {
  const { user } = useContext(AuthContext);
  const URL_PEDIDOS = process.env.REACT_APP_URL_PEDIDOS;
  const URL_EMPRESAS = process.env.REACT_APP_URL_EMPRESAS;
  const URL_PEDIDOS_POR_FACTURAR = process.env.REACT_APP_URL_PEDIDOS_POR_FACTURAR;
  const URL_FACTURAS_DESDE_PEDIDO = process.env.REACT_APP_URL_FACTURAS_DESDE_PEDIDO;

  const hoy = moment(new Date()).format("YYYY-MM-DD");

  const [fecha, setFecha] = useState(hoy);
  const [pedidos, setPedidos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState("");
  const [selectedEmpresa, setSelectedEmpresa] = useState("");
  const [cliente, setCliente] = useState({ nombre: "", id: "" });
  const [observaciones, setObservaciones] = useState("NA");
  const [renglones, setRenglones] = useState([]);
  const [validaBoton, setValidaBoton] = useState(true);

  const cabeceras = () => ({
    headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
  });

  useEffect(() => {
    axios
      .get(URL_PEDIDOS, cabeceras())
      .then((res) => setPedidos(res.data))
      .catch((err) => console.log(err));
    axios
      .get(URL_EMPRESAS, cabeceras())
      .then((res) => setEmpresas(res.data))
      .catch((err) => console.log(err));
  }, []);

  /** Trae lo que le falta por facturar al pedido elegido. */
  function elegirPedido(id) {
    setSelectedPedido(id);
    setRenglones([]);
    if (!id) return setCliente({ nombre: "", id: "" });

    const pedido = pedidos.find((p) => p._id === id);
    if (pedido && pedido.clientes && pedido.clientes?.[0]) {
      setCliente({ nombre: pedido.clientes?.[0]?.nombre_comercial, id: pedido.clientes?.[0]?._id });
    }

    axios
      .get(`${URL_PEDIDOS_POR_FACTURAR}/${id}`, cabeceras())
      .then((res) => {
        // Arranca proponiendo facturar todo lo pendiente; se puede bajar.
        setRenglones(res.data.map((r) => ({ ...r, aFacturar: r.pendienteFacturar })));
      })
      .catch((err) => console.log(err));
  }

  const cambiaCantidad = (id, valor) =>
    setRenglones(
      renglones.map((r) => {
        if (r._id !== id) return r;
        const cantidad = Math.min(Math.max(parseFloat(valor) || 0, 0), r.pendienteFacturar);
        return { ...r, aFacturar: cantidad };
      })
    );

  const totalPiezas = renglones.reduce((t, r) => t + (parseFloat(r.aFacturar) || 0), 0);
  const totalImporte =
    Math.round(renglones.reduce((t, r) => t + (parseFloat(r.aFacturar) || 0) * r.precio, 0) * 100) / 100;

  const guardar = async (event) => {
    event.preventDefault();
    const aMandar = renglones
      .filter((r) => parseFloat(r.aFacturar) > 0)
      .map((r) => ({ articulosPedido: r._id, cantidad: parseFloat(r.aFacturar), precio: r.precio }));

    if (aMandar.length === 0) {
      return Swal.fire({ icon: "warning", title: "Sin renglones", text: "Captura cuanto vas a facturar" });
    }

    setValidaBoton(false);
    try {
      const { data } = await axios.post(
        URL_FACTURAS_DESDE_PEDIDO,
        {
          fecha,
          pedidos: selectedPedido,
          clientes: cliente.id,
          empresas: selectedEmpresa,
          observaciones,
          subTotal: totalImporte,
          total: totalImporte,
          renglones: aMandar,
        },
        cabeceras()
      );
      Swal.fire({
        icon: "success",
        title: "Factura creada",
        text: `${data.piezas} pzas en ${data.renglones} renglon(es)`,
      });
      setTimeout(() => {
        window.location.href = "/ListadoFacturas";
      }, 1500);
    } catch (error) {
      const info = error.response ? error.response.data : {};
      setValidaBoton(true);
      // El back rechaza si algun renglon se pasa de lo pendiente, y los lista.
      const excesos = (info.excesos || [])
        .map(
          (e) =>
            `${e.articulo}${e.color ? ` ${e.color}` : ""}${e.talla ? ` ${e.talla}` : ""}: ` +
            `pendiente ${e.pendiente}, se pidieron ${e.solicitado}`
        )
        .join("<br/>");
      Swal.fire({
        icon: "error",
        title: "No se pudo facturar",
        html: excesos || info.message || "Algo salio mal",
      });
    }
  };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_facturas ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-file-invoice-dollar" style={{ marginRight: 8, color: "#0ea5e9" }} />
                Facturar Pedido
              </h3>
              <p className="page-subtitle">
                Total o parcial, por talla y color. Facturar no mueve inventario.
              </p>
            </div>
          </div>

          <Form onSubmit={guardar}>
            <div className="form-section">
              <div className="form-section-header">
                <i className="fas fa-info-circle" />
                <span className="form-section-title">Datos Generales</span>
              </div>
              <div className="form-section-body">
                <Row>
                  <Col md={2}>
                    <Label>Fecha</Label>
                    <Input type="date" value={fecha} required onChange={(e) => setFecha(e.target.value)} />
                  </Col>
                  <Col md={3}>
                    <Label>Pedido</Label>
                    <Input
                      type="select"
                      value={selectedPedido}
                      required
                      onChange={(e) => elegirPedido(e.target.value)}
                    >
                      <option value="">Selecciona</option>
                      {pedidos
                        .sort((a, b) => (a.idPedido < b.idPedido ? 1 : -1))
                        .map((a) => (
                          <option value={a._id} key={a._id}>
                            {a.idPedido} · {a.clientes && a.clientes?.[0] ? a.clientes?.[0]?.nombre_comercial : ""}
                          </option>
                        ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label>Cliente</Label>
                    <Input type="text" value={cliente.nombre} disabled />
                  </Col>
                  <Col md={2}>
                    <Label>Empresa</Label>
                    <Input
                      type="select"
                      value={selectedEmpresa}
                      required
                      onChange={(e) => setSelectedEmpresa(e.target.value)}
                    >
                      <option value="">Selecciona</option>
                      {empresas.map((a) => (
                        <option value={a._id} key={a._id}>
                          {a.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label>Observaciones</Label>
                    <Input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                  </Col>
                </Row>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-header">
                <i className="fas fa-tshirt" />
                <span className="form-section-title">Pendiente de facturar</span>
              </div>
              <div className="form-section-body">
                <Table size="sm" borderless>
                  <thead>
                    <tr>
                      <th>Articulo</th>
                      <th>Color</th>
                      <th>Talla</th>
                      <th>Pedido</th>
                      <th>Surtido</th>
                      <th>Facturado</th>
                      <th>Pendiente</th>
                      <th>A facturar</th>
                      <th>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renglones.map((r) => (
                      <tr key={r._id}>
                        <td>{r.articulos ? r.articulos.descripcion : ""}</td>
                        <td>{r.colores ? r.colores.name : "-"}</td>
                        <td>{r.tallas ? r.tallas.name : "-"}</td>
                        <td>{r.cantidad}</td>
                        <td>{r.surtido}</td>
                        <td>{r.facturado}</td>
                        <td>{r.pendienteFacturar}</td>
                        <td style={{ width: 120 }}>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            max={r.pendienteFacturar}
                            value={r.aFacturar}
                            onChange={(e) => cambiaCantidad(r._id, e.target.value)}
                          />
                        </td>
                        <td>
                          {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                            (parseFloat(r.aFacturar) || 0) * r.precio
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {selectedPedido && renglones.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#94a3b8" }}>
                    Este pedido ya esta facturado por completo
                  </p>
                ) : undefined}
                {renglones.length > 0 ? (
                  <h5 style={{ textAlign: "right" }}>
                    {totalPiezas} pzas ·{" "}
                    {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(totalImporte)}
                  </h5>
                ) : undefined}
              </div>
            </div>

            <div className="submit-bar">
              <Button href="/ListadoFacturas" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
              <Button type="submit" className="btn btn-success" disabled={!validaBoton || totalPiezas === 0}>
                <i className="fas fa-save" style={{ marginRight: 6 }} /> Facturar
              </Button>
            </div>
          </Form>
          <br />
        </div>
      ) : undefined}
    </>
  );
}

export default FacturasPedidoCreate;
